import { Component, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-listbox',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ListboxComponent),
      multi: true
    }
  ],
  template: `
    <div class="listbox" [ngStyle]="listStyle">
      @if (multiple && checkbox) {
        <div class="listbox-item" style="border-bottom:1px solid var(--color-border-light);font-weight:600;"
             (click)="toggleAll()">
          <input type="checkbox" class="form-checkbox" [checked]="allSelected" [indeterminate]="someSelected" readonly>
          <span>Alle</span>
        </div>
      }
      @for (opt of options; track optionValue ? opt[optionValue] : opt) {
        <div class="listbox-item" [class.selected]="isSelected(opt)" (click)="onItemClick(opt)">
          @if (multiple && checkbox) {
            <input type="checkbox" class="form-checkbox" [checked]="isSelected(opt)" readonly>
          }
          <span>{{ getLabel(opt) }}</span>
        </div>
      }
    </div>
  `
})
export class ListboxComponent implements ControlValueAccessor {
  @Input() options: any[] = [];
  @Input() optionLabel = 'label';
  @Input() optionValue = '';
  @Input() multiple = false;
  @Input() checkbox = false;
  @Input() listStyle: { [key: string]: string } | undefined;

  value: any = null;

  private onChange: (val: any) => void = () => {};
  private onTouched: () => void = () => {};

  getLabel(opt: any): string {
    if (opt == null) return '';
    return typeof opt === 'object' ? opt[this.optionLabel] ?? '' : String(opt);
  }

  getValue(opt: any): any {
    if (opt == null) return opt;
    return this.optionValue && typeof opt === 'object' ? opt[this.optionValue] : opt;
  }

  isSelected(opt: any): boolean {
    const val = this.getValue(opt);
    if (this.multiple && Array.isArray(this.value)) {
      return this.value.includes(val);
    }
    return this.value === val;
  }

  get allSelected(): boolean {
    return Array.isArray(this.value) && this.options?.length > 0 && this.value.length === this.options.length;
  }

  get someSelected(): boolean {
    return Array.isArray(this.value) && this.value.length > 0 && this.value.length < (this.options?.length ?? 0);
  }

  onItemClick(opt: any): void {
    const val = this.getValue(opt);
    if (this.multiple) {
      const current: any[] = Array.isArray(this.value) ? [...this.value] : [];
      const idx = current.indexOf(val);
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        current.push(val);
      }
      this.value = current;
    } else {
      this.value = val;
    }
    this.onChange(this.value);
    this.onTouched();
  }

  toggleAll(): void {
    if (this.allSelected) {
      this.value = [];
    } else {
      this.value = (this.options || []).map(opt => this.getValue(opt));
    }
    this.onChange(this.value);
    this.onTouched();
  }

  writeValue(val: any): void {
    this.value = val;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
}
